import React, { useCallback, useEffect, useMemo, useState } from 'react';

import CopyToClipboard from 'react-copy-to-clipboard';
import {
  FiSearch,
  FiFileText,
  FiDownload,
  FiCopy,
  FiXCircle,
  FiPrinter,
  FiAlertCircle,
} from 'react-icons/fi';

import { Divider, Modal } from '@material-ui/core';

import api from '../../services/api';
import { useAuth } from '../../hooks/auth';
import { useToast } from '../../hooks/toast';
import readBlobAsText from '../../utils/readBlobAsText';

import * as S from './styles';

const JUSTIFICATIVA_MIN = 15;

/** Motivo da rejeição/denegação devolvido pela SEFAZ (atac-notas). */
interface Rejeicao {
  cStat?: string;
  xMotivo?: string;
  dhRejeicao?: string;
}

interface Nota {
  id?: string | number;
  numero?: number | string;
  serie?: number | string;
  chaveAcesso?: string;
  dataEmissao?: string;
  status?: string;
  valorTotal?: number;
  cpfDestinatario?: string;
  cnpjDestinatario?: string;
  nomeDestinatario?: string;
  rejeicao?: Rejeicao;
  [key: string]: any;
}

interface Meta {
  total?: number;
  page?: number;
  limit?: number;
  totalPages?: number;
  [key: string]: any;
}

interface Filtros {
  dataInicio: string;
  dataFim: string;
  status: string;
  serie: string;
  cpf: string;
  valor: string;
}

const LIMIT = 20;

const STATUS_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'authorized', label: 'Autorizada' },
  { value: 'pending', label: 'Pendente' },
  { value: 'processing', label: 'Processando' },
  { value: 'rejected', label: 'Rejeitada' },
  { value: 'denied', label: 'Denegada' },
  { value: 'cancelled', label: 'Cancelada' },
  { value: 'contingency_pending', label: 'Contingência' },
];

const getFirstDayOfMonth = (): string => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  return firstDay.toISOString().split('T')[0];
};

const getLastDayOfMonth = (): string => {
  const now = new Date();
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return lastDay.toISOString().split('T')[0];
};

/** Mapeia o status da NFC-e para a cor do badge (02-tecnico.md:122). */
const corDoStatus = (
  status?: string,
): 'verde' | 'vermelho' | 'amarelo' | 'cinza' => {
  const s = String(status || '').toLowerCase();
  if (s === 'authorized' || s === 'autorizada') return 'verde';
  if (
    [
      'rejected',
      'denied',
      'cancelled',
      'canceled',
      'rejeitada',
      'denegada',
      'cancelada',
    ].includes(s)
  )
    return 'vermelho';
  if (
    [
      'pending',
      'processing',
      'contingency_pending',
      'pendente',
      'processando',
    ].includes(s)
  )
    return 'amarelo';
  return 'cinza';
};

/** Rótulo em português do status devolvido pela atac-notas. */
const STATUS_LABELS: Record<string, string> = {
  draft: 'Rascunho',
  pending: 'Pendente',
  processing: 'Processando',
  authorized: 'Autorizada',
  rejected: 'Rejeitada',
  denied: 'Denegada',
  cancelled: 'Cancelada',
  canceled: 'Cancelada',
  contingency_pending: 'Contingência',
};

const rotuloDoStatus = (status?: string): string => {
  const s = String(status || '').toLowerCase();
  return STATUS_LABELS[s] || status || '-';
};

/** Rejeitada/denegada: a SEFAZ não autorizou, mas o XML transmitido existe. */
const foiRejeitada = (nota: Nota): boolean => {
  const s = String(nota.status || '').toLowerCase();
  return s === 'rejected' || s === 'denied' || s === 'rejeitada';
};

/** DANFE só existe a partir do XML autorizado (ou da nota já cancelada). */
const temDanfe = (nota: Nota): boolean => {
  const s = String(nota.status || '').toLowerCase();
  return ['authorized', 'autorizada', 'cancelled', 'canceled', 'cancelada'].includes(
    s,
  );
};

/** Texto curto do motivo, para caber na coluna de status. */
const resumoDaRejeicao = (nota: Nota): string => {
  const { cStat, xMotivo } = nota.rejeicao || {};
  if (!cStat && !xMotivo) return '';
  return cStat ? `${cStat} — ${xMotivo || 'sem descrição'}` : String(xMotivo);
};

const formatarDataHora = (valor?: string): string => {
  if (!valor) return '-';
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return String(valor);
  return data.toLocaleString('pt-BR');
};

/**
 * Emissão na listagem: data e hora, em duas linhas para caber na coluna sem
 * alargar a tabela. Hora sem segundos — o que interessa ali é localizar a nota
 * no dia; o horário exato continua no XML/DANFE.
 */
const formatarEmissao = (
  valor?: string,
): { data: string; hora: string } | null => {
  if (!valor) return null;
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return null;
  return {
    data: data.toLocaleDateString('pt-BR'),
    hora: data.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    }),
  };
};

const formatarValor = (valor?: number): string => {
  if (valor === undefined || valor === null) return '-';
  return valor.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

const NotasEmitidas: React.FC = () => {
  const { user } = useAuth();
  const { addToast } = useToast();

  const [filtros, setFiltros] = useState<Filtros>({
    dataInicio: getFirstDayOfMonth(),
    dataFim: getLastDayOfMonth(),
    status: '',
    serie: '',
    cpf: '',
    valor: '',
  });
  const [notas, setNotas] = useState<Nota[]>([]);
  const [meta, setMeta] = useState<Meta>({});
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [notaCancelar, setNotaCancelar] = useState<Nota | null>(null);
  const [notaRejeitada, setNotaRejeitada] = useState<Nota | null>(null);
  const [justificativaCancel, setJustificativaCancel] = useState('');
  const [cancelando, setCancelando] = useState(false);

  const empresaId = user.empresa_id;

  const carregar = useCallback(
    async (paginaAlvo: number) => {
      setLoading(true);
      try {
        // A3: datas enviadas como dataInicio/dataFim (NUNCA startDate/endDate).
        const params: Record<string, string | number> = {
          page: paginaAlvo,
          limit: LIMIT,
        };
        if (filtros.dataInicio) params.dataInicio = filtros.dataInicio;
        if (filtros.dataFim) params.dataFim = filtros.dataFim;
        if (filtros.status) params.status = filtros.status;
        if (filtros.serie) params.serie = filtros.serie;
        if (filtros.cpf) params.cpfDestinatario = filtros.cpf;

        const { data } = await api.get(`/companies/${empresaId}/nfce`, {
          params,
        });

        const lista: Nota[] = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
          ? data
          : [];

        setNotas(lista);
        setMeta(data?.meta || {});
        setPage(paginaAlvo);
      } catch (err) {
        addToast({
          type: 'error',
          title: 'Erro ao carregar notas',
          description: 'Não foi possível consultar as notas emitidas.',
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast, empresaId, filtros],
  );

  useEffect(() => {
    carregar(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      carregar(1);
    },
    [carregar],
  );

  const handleLimpar = useCallback(() => {
    setFiltros({
      dataInicio: getFirstDayOfMonth(),
      dataFim: getLastDayOfMonth(),
      status: '',
      serie: '',
      cpf: '',
      valor: '',
    });
  }, []);

  const handleChange = useCallback(
    (campo: keyof Filtros) =>
      (
        event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
      ): void => {
        const { value } = event.target;
        setFiltros((state) => ({ ...state, [campo]: value }));
      },
    [],
  );

  /** Filtro de valor é aplicado client-side (atac-notas não tem param de valor). */
  const notasExibidas = useMemo(() => {
    if (!filtros.valor) return notas;
    const minimo = Number(filtros.valor);
    if (Number.isNaN(minimo)) return notas;
    return notas.filter((nota) => {
      const valor = Number(nota.valorTotal ?? nota.valor ?? 0);
      return valor >= minimo;
    });
  }, [notas, filtros.valor]);

  /** Janela de cancelamento de 30 min (RN5) — habilita só o gatilho/placeholder. */
  const podeCancelar = useCallback((nota: Nota): boolean => {
    const status = String(nota.status || '').toLowerCase();
    if (status !== 'authorized' && status !== 'autorizada') return false;
    const emissao = new Date(
      nota.dataEmissao || nota.data_emissao || nota.dataAutorizacao || '',
    );
    if (Number.isNaN(emissao.getTime())) return false;
    const minutos = (Date.now() - emissao.getTime()) / 60000;
    return minutos <= 30;
  }, []);

  const baixarArquivo = useCallback(
    async (chave: string, tipo: 'xml' | 'danfe'): Promise<boolean> => {
      if (!chave) {
        addToast({ type: 'error', title: 'Nota sem chave de acesso' });
        return false;
      }
      try {
        const url =
          tipo === 'xml'
            ? `/companies/${empresaId}/nfce/${chave}/xml`
            : `/companies/${empresaId}/nfce/${chave}/danfe?format=pdf`;

        const response = await api.get(url, { responseType: 'blob' });

        const mime = tipo === 'xml' ? 'application/xml' : 'application/pdf';
        const ext = tipo === 'xml' ? 'xml' : 'pdf';

        const blobUrl = window.URL.createObjectURL(
          new Blob([response.data], { type: mime }),
        );
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', `${chave}.${ext}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
        return true;
      } catch (err) {
        // O corpo de erro chega como Blob (responseType: 'blob'); extrai a
        // mensagem normalizada do backend quando houver.
        let description: string | undefined;
        const data = (err as any)?.response?.data;
        if (data instanceof Blob) {
          try {
            const texto = await readBlobAsText(data);
            description = JSON.parse(texto)?.message;
          } catch {
            description = undefined;
          }
        }
        addToast({
          type: 'error',
          title: tipo === 'xml' ? 'Erro ao obter XML' : 'Erro ao obter DANFE',
          description,
        });
        return false;
      }
    },
    [addToast, empresaId],
  );

  /** Abre o modal de cancelamento para a nota (Story 4-1 / T4.2). */
  const handleCancelar = useCallback((nota: Nota) => {
    setJustificativaCancel('');
    setNotaCancelar(nota);
  }, []);

  const handleConfirmarCancelamento = useCallback(async () => {
    if (!notaCancelar) return;

    const chave = String(notaCancelar.chaveAcesso || '');
    const just = justificativaCancel.trim();

    if (!chave) {
      addToast({ type: 'error', title: 'Nota sem chave de acesso' });
      return;
    }
    if (just.length < JUSTIFICATIVA_MIN) {
      addToast({
        type: 'error',
        title: 'Justificativa muito curta',
        description: `Informe ao menos ${JUSTIFICATIVA_MIN} caracteres.`,
      });
      return;
    }

    try {
      setCancelando(true);
      await api.post(`/companies/${empresaId}/nfce/${chave}/cancelar`, {
        justificativa: just,
      });
      addToast({
        type: 'success',
        title: 'NFC-e cancelada',
        description: `Nota ${notaCancelar.numero ?? ''} cancelada com sucesso.`,
      });
      setNotaCancelar(null);
      setJustificativaCancel('');
      carregar(page);
    } catch (err) {
      const description = (err as any)?.response?.data?.message;
      addToast({ type: 'error', title: 'Erro ao cancelar NFC-e', description });
    } finally {
      setCancelando(false);
    }
  }, [notaCancelar, justificativaCancel, empresaId, addToast, carregar, page]);

  /** Reemite (regenera) o DANFE a partir do XML autorizado (T4.1). */
  const handleReimprimirDanfe = useCallback(
    async (chave: string) => {
      const ok = await baixarArquivo(chave, 'danfe');
      if (ok) {
        addToast({
          type: 'success',
          title: 'DANFE reemitido',
          description: 'Gerado novamente a partir do XML autorizado.',
        });
      }
    },
    [baixarArquivo, addToast],
  );

  const totalPaginas =
    meta.totalPages ||
    (meta.total ? Math.ceil(Number(meta.total) / LIMIT) : undefined);

  return (
    <S.Container>
      <div className="header">
        <h2>Notas Emitidas</h2>
        <p>
          Consulte as NFC-e emitidas da sua empresa, veja o motivo das notas
          rejeitadas, baixe XML/DANFE e copie a chave de acesso.
        </p>
      </div>

      <Divider />

      <S.Filtros onSubmit={handleSubmit}>
        <div className="campo">
          <label htmlFor="dataInicio">Data início</label>
          <input
            id="dataInicio"
            type="date"
            value={filtros.dataInicio}
            onChange={handleChange('dataInicio')}
          />
        </div>
        <div className="campo">
          <label htmlFor="dataFim">Data fim</label>
          <input
            id="dataFim"
            type="date"
            value={filtros.dataFim}
            onChange={handleChange('dataFim')}
          />
        </div>
        <div className="campo">
          <label htmlFor="status">Status</label>
          <select
            id="status"
            value={filtros.status}
            onChange={handleChange('status')}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="campo">
          <label htmlFor="serie">Série</label>
          <input
            id="serie"
            type="number"
            placeholder="Ex.: 1"
            value={filtros.serie}
            onChange={handleChange('serie')}
          />
        </div>
        <div className="campo">
          <label htmlFor="cpf">CPF destinatário</label>
          <input
            id="cpf"
            type="text"
            placeholder="Somente números"
            value={filtros.cpf}
            onChange={handleChange('cpf')}
          />
        </div>
        <div className="campo">
          <label htmlFor="valor">Valor mín. (R$)</label>
          <input
            id="valor"
            type="number"
            step="0.01"
            placeholder="Ex.: 50,00"
            value={filtros.valor}
            onChange={handleChange('valor')}
          />
        </div>
        <div className="acoes-filtro">
          <button type="submit" disabled={loading}>
            <FiSearch size={16} />
            {loading ? 'Buscando...' : 'Buscar'}
          </button>
          <button
            type="button"
            className="limpar"
            onClick={handleLimpar}
            disabled={loading}
          >
            Limpar
          </button>
        </div>
      </S.Filtros>

      <S.ContainerList>
        <S.TableHeader>
          <span>Número/Série</span>
          <span>Chave de acesso</span>
          <span>Emissão</span>
          <span>Status</span>
          <span>Valor</span>
          <span>CPF</span>
          <span>Ações</span>
        </S.TableHeader>

        {notasExibidas.length === 0 && !loading && (
          <S.EmptyState>Nenhuma nota encontrada para os filtros selecionados.</S.EmptyState>
        )}

        {loading && <S.EmptyState>Carregando...</S.EmptyState>}

        {!loading &&
          notasExibidas.map((nota, index) => {
            const chave = String(nota.chaveAcesso || '');
            const cancelavel = podeCancelar(nota);
            const rejeitada = foiRejeitada(nota);
            const danfeDisponivel = temDanfe(nota);
            const emissao = formatarEmissao(
              nota.dataEmissao || nota.data_emissao || nota.createdAt,
            );
            return (
              <S.TableRow key={chave || nota.id || index}>
                <p>
                  {nota.numero ?? '-'}
                  {nota.serie ? ` / ${nota.serie}` : ''}
                </p>
                <p className="chave" title={chave}>
                  {chave || '-'}
                </p>
                <S.EmissaoCell>
                  {emissao ? (
                    <>
                      <p>{emissao.data}</p>
                      <span>{emissao.hora}</span>
                    </>
                  ) : (
                    <p>-</p>
                  )}
                </S.EmissaoCell>
                <S.StatusCell>
                  <S.StatusBadge cor={corDoStatus(nota.status)}>
                    {rotuloDoStatus(nota.status)}
                  </S.StatusBadge>
                  {rejeitada && !!resumoDaRejeicao(nota) && (
                    <button
                      type="button"
                      className="motivo"
                      title={resumoDaRejeicao(nota)}
                      onClick={() => setNotaRejeitada(nota)}
                    >
                      {resumoDaRejeicao(nota)}
                    </button>
                  )}
                </S.StatusCell>
                <p>{formatarValor(nota.valorTotal ?? nota.valor)}</p>
                <p>{nota.cpfDestinatario || nota.cnpjDestinatario || '-'}</p>
                <S.Acoes>
                  <button
                    type="button"
                    title={
                      rejeitada
                        ? 'Ver XML transmitido (sem valor fiscal)'
                        : 'Ver XML'
                    }
                    onClick={() => baixarArquivo(chave, 'xml')}
                    disabled={!chave}
                  >
                    <FiFileText size={18} />
                  </button>
                  {rejeitada && (
                    <button
                      type="button"
                      className="rejeicao"
                      title="Ver motivo da rejeição"
                      onClick={() => setNotaRejeitada(nota)}
                    >
                      <FiAlertCircle size={18} />
                    </button>
                  )}
                  <button
                    type="button"
                    title={
                      danfeDisponivel
                        ? 'Baixar DANFE (PDF)'
                        : 'DANFE disponível apenas para nota autorizada'
                    }
                    onClick={() => baixarArquivo(chave, 'danfe')}
                    disabled={!chave || !danfeDisponivel}
                  >
                    <FiDownload size={18} />
                  </button>
                  <button
                    type="button"
                    title={
                      danfeDisponivel
                        ? 'Reimprimir DANFE'
                        : 'DANFE disponível apenas para nota autorizada'
                    }
                    onClick={() => handleReimprimirDanfe(chave)}
                    disabled={!chave || !danfeDisponivel}
                  >
                    <FiPrinter size={18} />
                  </button>
                  <CopyToClipboard
                    text={chave}
                    onCopy={() =>
                      addToast({ type: 'success', title: 'Copiado!' })
                    }
                  >
                    <span className="copiar" title="Copiar chave de acesso">
                      <FiCopy size={18} />
                    </span>
                  </CopyToClipboard>
                  {cancelavel && (
                    <button
                      type="button"
                      className="cancelar"
                      title="Cancelar NFC-e (até 30 min)"
                      onClick={() => handleCancelar(nota)}
                    >
                      <FiXCircle size={18} />
                    </button>
                  )}
                </S.Acoes>
              </S.TableRow>
            );
          })}
      </S.ContainerList>

      <S.Paginacao>
        <button
          type="button"
          onClick={() => carregar(page - 1)}
          disabled={loading || page <= 1}
        >
          Anterior
        </button>
        <span>
          Página {page}
          {totalPaginas ? ` de ${totalPaginas}` : ''}
        </span>
        <button
          type="button"
          onClick={() => carregar(page + 1)}
          disabled={
            loading ||
            (totalPaginas ? page >= totalPaginas : notas.length < LIMIT)
          }
        >
          Próxima
        </button>
      </S.Paginacao>

      <Modal open={!!notaRejeitada} onClose={() => setNotaRejeitada(null)}>
        <S.ModalBox>
          <h3>Motivo da rejeição</h3>
          <p className="nota">
            Nota {notaRejeitada?.numero ?? '-'}
            {notaRejeitada?.serie ? ` / série ${notaRejeitada.serie}` : ''}
          </p>
          <p className="chave">{notaRejeitada?.chaveAcesso || '-'}</p>
          <S.RejeicaoDetalhe>
            <span className="rotulo">Código SEFAZ</span>
            <strong>{notaRejeitada?.rejeicao?.cStat || '-'}</strong>
            <span className="rotulo">Descrição</span>
            <strong>
              {notaRejeitada?.rejeicao?.xMotivo ||
                'A SEFAZ não devolveu a descrição da rejeição.'}
            </strong>
            <span className="rotulo">Data da rejeição</span>
            <strong>
              {formatarDataHora(notaRejeitada?.rejeicao?.dhRejeicao)}
            </strong>
          </S.RejeicaoDetalhe>
          <p className="aviso">
            A nota não foi autorizada. Corrija o cadastro apontado no motivo e
            emita novamente. O XML transmitido pode ser baixado para conferência
            — ele não tem valor fiscal.
          </p>
          <div className="acoes">
            <button
              type="button"
              className="voltar"
              onClick={() => setNotaRejeitada(null)}
            >
              Fechar
            </button>
            <button
              type="button"
              className="primario"
              onClick={() =>
                baixarArquivo(String(notaRejeitada?.chaveAcesso || ''), 'xml')
              }
              disabled={!notaRejeitada?.chaveAcesso}
            >
              Baixar XML transmitido
            </button>
          </div>
        </S.ModalBox>
      </Modal>

      <Modal
        open={!!notaCancelar}
        onClose={() => {
          if (!cancelando) setNotaCancelar(null);
        }}
      >
        <S.ModalBox>
          <h3>Cancelar NFC-e</h3>
          <p className="nota">
            Nota {notaCancelar?.numero ?? '-'}
            {notaCancelar?.serie ? ` / série ${notaCancelar.serie}` : ''}
          </p>
          <p className="chave">{notaCancelar?.chaveAcesso || '-'}</p>
          <p className="aviso">
            Permitido em até 30 minutos da autorização. Informe a justificativa
            (mínimo {JUSTIFICATIVA_MIN} caracteres).
          </p>
          <textarea
            value={justificativaCancel}
            onChange={(e) => setJustificativaCancel(e.target.value)}
            maxLength={255}
            rows={4}
            placeholder="Justificativa do cancelamento"
            disabled={cancelando}
          />
          <span className="contador">
            {justificativaCancel.trim().length}/255
          </span>
          <div className="acoes">
            <button
              type="button"
              className="voltar"
              onClick={() => setNotaCancelar(null)}
              disabled={cancelando}
            >
              Voltar
            </button>
            <button
              type="button"
              className="confirmar"
              onClick={handleConfirmarCancelamento}
              disabled={
                cancelando ||
                justificativaCancel.trim().length < JUSTIFICATIVA_MIN
              }
            >
              {cancelando ? 'Cancelando...' : 'Confirmar cancelamento'}
            </button>
          </div>
        </S.ModalBox>
      </Modal>
    </S.Container>
  );
};

export default NotasEmitidas;
